import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Radio from '@mui/material/Radio';
import useMediaQuery from '@mui/material/useMediaQuery';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTheme } from '@mui/material/styles';
import { useRouter } from 'next/router';
import NextImage from 'next/image';
import { useDispatch, useSelector } from 'react-redux';
import {
  StScheduleWrapper,
  StFormCard,
  StHeading,
  StSubHeading,
  StSiteCard,
  StSiteDetail,
  StSiteMetaRow,
  StDateLabel,
  StMonthLabel,
  StDateCarouselWrapper,
  StNavArrow,
  StDayCell,
  StTimeSlotsContainer,
  StTimeSlotItem,
  StTimeLabel,
  StSlotsLeftBadge,
  StConfirmButton,
  StAlertBanner,
} from './FOVScheduling.styles';
import { bookAppointment, updateCDPAppointmentStatus } from './FOVScheduling.api';
import { DaySlot, FOVSchedulingProps, SchedulingStatus, TimeSlot } from './FOVScheduling.types';
import {
  setAvailableDays,
  setFOVEPrId,
  setFOVRedirectUrl,
} from '../../features/fovSchedulingSlice';
import { showLoader, hideLoader } from '../../features/loaderSlice';
import { RootState } from '../../store/store';
import useApplyV2Theme from '../../lib/hooks/useApplyV2Theme';
import { decryptEPrId } from '../../lib/utils/fovToken';
import ButtonPrimary from '../shared/Button/PrimaryButton';
import ButtonDiscreet from '../shared/Button/ButtonDiscreet';
import SiteLocationRichText from 'components/SiteLocationRichText/SiteLocationRichText';
import {
  FOV_APPOINTMENT_STATUS,
  FOV_BOOKING_STATUS,
  FOV_INTEGRATION_SOURCE,
  FOV_SOURCE,
} from '../../constants';
const VISIBLE_DAYS = 7;
/** Converts "YYYY-MM-DD" date string → "MONTH YYYY" carousel header label. Falls back to current month. */
const getMonthLabel = (dateStr?: string): string => {
  if (!dateStr) {
    const now = new Date();
    return now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).toUpperCase();
  }
  const d = new Date(`${dateStr}T00:00`);
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).toUpperCase();
};
/** Converts "9:00 AM" → "09:00:00" for the BookAppointment API. */
const toHHMMSS = (t?: string): string | null => {
  if (!t) return null;
  try {
    const d = new Date(`1970-01-01 ${t}`);
    if (isNaN(d.getTime())) return null;
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}:00`;
  } catch {
    return null;
  }
};
/**
 * Formats a slot time to 12-hour "h:mm AM/PM" form for display.
 */
const formatTimeToAmPm = (t?: string): string => {
  if (!t) return '';
  const value = t.trim();
  const parsed = new Date(`1970-01-01 ${value}`);
  if (isNaN(parsed.getTime())) return value;
  return parsed
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    .replace(/\u202f/g, ' ');
};
// ─── Component ────────────────────────────────────────────────────────────────
const FOVScheduling = (props: FOVSchedulingProps): JSX.Element => {
  const theme = useTheme();
  const router = useRouter();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isToapplyV2Theme } = useApplyV2Theme();
  // ─── Redux ────────────────────────────────────────────────────────────────
  const storedDays = useSelector((state: RootState) => state.fovScheduling.availableDays);
  const storedTimezone = useSelector((state: RootState) => state.fovScheduling.timezone);
  const selectedPlace = useSelector((state: RootState) => state.selectedSite);
  const isLoading = useSelector((state: RootState) => state.loader.isLoading);
  // ───  ePrId, redirectUrl and integration type from query ──────────
  const encryptedEPrId: string | null = (router.query['ePrId'] as string | undefined) || null;
  // ePrId decrypted from the query (null until decoded / when not present).
  const [decryptedEPrId, setDecryptedEPrId] = useState<string | null>(null);
  // Decrypt the ePrId query param (if present).
  useEffect(() => {
    if (!encryptedEPrId) {
      setDecryptedEPrId(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const decoded = await decryptEPrId(encryptedEPrId);
      if (cancelled) return;
      setDecryptedEPrId(decoded);
    })();
    return () => {
      cancelled = true;
    };
  }, [encryptedEPrId]);
  // Effective values: decrypted ePrId + plain RedirectUrl / type query params.
  const ePrId: string | null = decryptedEPrId;
  const redirectUrl: string = (router.query['RedirectUrl'] as string | undefined) || '';
  // Only call status-update APIs when this page is rendered in integrated mode
  const isIntegrated: boolean = router.query['type'] === 'integrated';
  // ─── State ────────────────────────────────────────────────────────────────
  const [days, setDays] = useState<DaySlot[]>(storedDays);
  const firstAvailableIndex = days.findIndex((d) => d.slotCount > 0);
  const defaultDayIndex = firstAvailableIndex >= 0 ? firstAvailableIndex : 0;
  const [selectedDayIndex, setSelectedDayIndex] = useState<number>(defaultDayIndex);
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  const [pageStart, setPageStart] = useState<number>(0);
  const [status, setStatus] = useState<SchedulingStatus>('idle');
  const conflictBannerRef = useRef<HTMLDivElement>(null);
  // Loader state for the "None of these times work for me" link.
  const [isNoneSubmitting, setIsNoneSubmitting] = useState<boolean>(false);
  // ─── Persist ePrId / redirectUrl in Redux ─────────────────────────────────
  useEffect(() => {
    if (ePrId) dispatch(setFOVEPrId(ePrId));
    dispatch(setFOVRedirectUrl(redirectUrl));
  }, [ePrId, redirectUrl, dispatch]);
  useEffect(() => {
    if (status !== 'conflict' || isLoading) return;

    const timer = window.setTimeout(() => {
      const banner = conflictBannerRef.current;
      if (!banner) return;

      const headerHeight = document.querySelector('.header')?.getBoundingClientRect().height ?? 0;
      const scrollMarginTop = parseFloat(window.getComputedStyle(banner).scrollMarginTop) || 0;
      const offset = Math.max(headerHeight, scrollMarginTop) + 8;
      const top = banner.getBoundingClientRect().top + window.scrollY - offset;

      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      banner.focus({ preventScroll: true });
    }, 200);

    return () => window.clearTimeout(timer);
  }, [status, isLoading]);
  // Guard — must be after all hooks
  if (!props?.fields) return <></>;
  const siteConfig = props.fields;
  const slotsLabelPlural = siteConfig?.slotsText?.value || 'SLOTS';
  const slotsLabelSingular = siteConfig?.slotsSingularText?.value || 'SLOT';
  const slotsLeftLabelPlural = siteConfig?.slotsLeftText?.value || 'SLOTS LEFT';
  const slotsLeftLabelSingular = siteConfig?.slotsLeftSingularText?.value || 'SLOT LEFT';
  const getSlotsLabel = (count: number): string =>
    count === 1 ? slotsLabelSingular : slotsLabelPlural;
  const getSlotsLeftLabel = (count: number): string =>
    count === 1 ? slotsLeftLabelSingular : slotsLeftLabelPlural;
  // ─── Derived state ────────────────────────────────────────────────────────
  // On mobile: show all days for scroll; on desktop: paginate with arrows
  const visibleDays: DaySlot[] = isMobile ? days : days.slice(pageStart, pageStart + VISIBLE_DAYS);
  const selectedDay: DaySlot = days[selectedDayIndex] ?? days[0];
  const timeSlots: TimeSlot[] = selectedDay?.timeSlots ?? [];
  const canGoBack = pageStart > 0;
  const canGoForward = pageStart + VISIBLE_DAYS < days.length;
  const isSubmitting = status === 'submitting';
  const themeColor = theme.palette.primary?.main || '#005587';
  // ─── Handlers ────────────────────────────────────────────────────────────
  const handleDayClick = (globalIndex: number) => {
    if (days[globalIndex].slotCount === 0) return;
    setSelectedDayIndex(globalIndex);
    // Clear any prior slot selection — the user must pick a time for the new day.
    setSelectedSlotId(null);
    setStatus('idle');
  };
  /**
   * "None of these times work for me"
   */
  const handleNoneOfTheseTimes = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (isNoneSubmitting) return;
    setIsNoneSubmitting(true);
    dispatch(showLoader());
    try {
      if (isIntegrated && ePrId) {
        try {
          await updateCDPAppointmentStatus({
            ePrId,
            appointmentStatus: FOV_APPOINTMENT_STATUS.NO_SLOTS_WORKED_FOR_SUBJECT,
            source: FOV_SOURCE,
          });
        } catch {
          // non-blocking — redirect regardless of API failure
        }
        bookAppointment({
          ePrId,
          appointmentStatus: FOV_APPOINTMENT_STATUS.NO_SLOTS_WORKED_FOR_SUBJECT,
          source: FOV_SOURCE,
        });
      }
      await router.push(redirectUrl);
    } finally {
      dispatch(hideLoader());
    }
  };
  /**
   * Confirm selected slot.
   */
  const handleConfirm = async () => {
    if (!selectedSlotId || isSubmitting) return;
    setStatus('submitting');
    dispatch(showLoader());
    const handleInvalidSlot = async (): Promise<void> => {
      const updatedDays = days.map((d, idx) => {
        if (idx !== selectedDayIndex) return d;
        const timeSlots = d.timeSlots.map((s) =>
          s.id === selectedSlotId ? { ...s, slotsLeft: 0 } : s
        );
        return {
          ...d,
          timeSlots,
          slotCount: timeSlots.reduce(
            (total, s) => (s.slotsLeft > 0 ? total + s.slotsLeft : total),
            0
          ),
        };
      });
      const remainingSelectableSlots = updatedDays.reduce(
        (count, d) => count + d.timeSlots.filter((s) => s.slotsLeft > 0).length,
        0
      );
      setDays(updatedDays);
      dispatch(setAvailableDays(updatedDays));
      if (remainingSelectableSlots <= 0) {
        if (isIntegrated) {
          try {
            await updateCDPAppointmentStatus({
              ePrId: ePrId!,
              appointmentStatus: FOV_APPOINTMENT_STATUS.NO_SLOTS_AVAILABLE_FOR_SUBJECT,
              source: FOV_SOURCE,
            });
          } catch {
            // non-blocking — redirect regardless
          }
          bookAppointment({
            ePrId: ePrId!,
            appointmentStatus: FOV_APPOINTMENT_STATUS.NO_SLOTS_AVAILABLE_FOR_SUBJECT,
            source: FOV_SOURCE,
          });
        }
        await router.push(redirectUrl);
        return;
      }
      setSelectedSlotId(null);
      setStatus('conflict');
    };
    try {
      const confirmedSlot = timeSlots.find((s) => s.id === selectedSlotId);
      if (ePrId) {
        const appointmentDateLocal = selectedDay?.date ?? null; // "YYYY-MM-DD"
        const appointmentTime = toHHMMSS(confirmedSlot?.startTime);
        const timezone = storedTimezone ?? null;
        // // Req 1: validate the slot is still available before booking
        // let isValid = false;
        // let validation: Awaited<ReturnType<typeof validateSlot>> | null = null;
        // try {
        //   validation = await validateSlot(Number(selectedSlotId));
        //   isValid = validation?.isValid === true && (validation?.remainingCapacity ?? 0) > 0;
        // } catch {
        //   // On any error treat the slot as invalid
        //   isValid = false;
        // }
        // if (!isValid) {
        //   await handleInvalidSlot(validation?.remainingCapacity ?? 0);
        //   return;
        // }
        // // Wait 3 seconds before booking the valid slot.
        let bookingSucceeded = false;
        try {
          const bookingResult = await bookAppointment({
            ePrId,
            appointmentStatus: FOV_APPOINTMENT_STATUS.REQUESTED,
            slotId: Number(selectedSlotId),
            appointmentDateLocal,
            appointmentTime,
            appointmentTimezone: timezone,
            bookingStatus: FOV_BOOKING_STATUS.BOOKED,
            source: FOV_SOURCE,
            integrationSource: FOV_INTEGRATION_SOURCE,
            isHumanApiEnabled: selectedPlace?.siteInfo?.isHumanAPIEnabled ?? false,
            protocolId: selectedPlace?.siteInfo?.protocol,
          });
          bookingSucceeded = bookingResult?.success === true;
        } catch {
          bookingSucceeded = false;
        }
        if (!bookingSucceeded) {
          await handleInvalidSlot();
          return;
        }
        await updateCDPAppointmentStatus({
          ePrId,
          appointmentStatus: FOV_APPOINTMENT_STATUS.REQUESTED,
          slotId: Number(selectedSlotId),
          appointmentDateLocal,
          appointmentTime,
          appointmentTimezone: timezone,
          bookingStatus: FOV_BOOKING_STATUS.BOOKED,
          source: FOV_SOURCE,
        });
        await new Promise((resolve) => setTimeout(resolve, 2000)); // 2 seconds
      }
      await router.push(redirectUrl);
    } catch {
      setStatus('conflict');
    } finally {
      dispatch(hideLoader());
    }
  };
  // ─── Render helpers ───────────────────────────────────────────────────────
  const renderAlertBanner = () =>
    status === 'conflict' ? (
      <StAlertBanner
        ref={conflictBannerRef}
        tabIndex={-1}
        severity="error"
        role="alert"
        data-testid="slot-conflict-banner"
      >
        <ErrorOutlineIcon sx={{ fontSize: 18, mt: '1px', flexShrink: 0 }} />
        {siteConfig?.slotTakenWarningText?.value ||
          'Sorry the slot you were booking has already been booked. Please try a different slot'}
      </StAlertBanner>
    ) : null;
  const renderSiteCard = () => (
    <StSiteCard>
      <CheckCircleIcon sx={{ color: 'green', fontSize: 22, mt: '2px', flexShrink: 0 }} />
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          '& p:first-of-type': { marginTop: 0 },
          '& p:last-of-type': { marginBottom: 0 },
        }}
      >
        <SiteLocationRichText
          siteAddressFormat={{ value: siteConfig?.siteDescription?.value }}
          siteInfo={selectedPlace?.siteInfo}
          style={{ padding: 0 }}
        />
        <StSiteMetaRow>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {siteConfig?.phoneIcon?.value?.src ? (
              <NextImage
                src={siteConfig.phoneIcon.value.src}
                alt={siteConfig.phoneIcon.value.alt || 'phone'}
                width={Number(siteConfig.phoneIcon.value.width) || 14}
                height={Number(siteConfig.phoneIcon.value.height) || 14}
                style={{ flexShrink: 0 }}
              />
            ) : (
              <LocalPhoneOutlinedIcon sx={{ fontSize: 14, color: themeColor }} />
            )}
            <StSiteDetail sx={{ fontSize: '13px' }}>
              {selectedPlace?.siteInfo?.contactPhoneNumber}
            </StSiteDetail>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {siteConfig?.mapIcon?.value?.src ? (
              <NextImage
                src={siteConfig.mapIcon.value.src}
                alt={siteConfig.mapIcon.value.alt || 'location'}
                width={Number(siteConfig.mapIcon.value.width) || 16}
                height={Number(siteConfig.mapIcon.value.height) || 16}
                style={{ flexShrink: 0 }}
              />
            ) : (
              <LocationOnOutlinedIcon
                sx={{ fontSize: 14, color: theme.palette.neutral7?.main || '#595959' }}
              />
            )}
            <StSiteDetail sx={{ fontSize: '13px' }}>
              {selectedPlace?.siteInfo?.radius || selectedPlace?.siteInfo?.distance} mi
            </StSiteDetail>
          </Box>
        </StSiteMetaRow>
      </Box>
    </StSiteCard>
  );
  const renderDateCarousel = () => {
    const visiblePage = isMobile ? days : days.slice(pageStart, pageStart + VISIBLE_DAYS);
    const selectedDayDate = days[selectedDayIndex]?.date;
    const isSelectedDayVisible = visiblePage.some((d) => d.date === selectedDayDate);
    const activeMonthDate = isSelectedDayVisible
      ? selectedDayDate
      : visiblePage.find((d) => d.slotCount > 0)?.date ?? visiblePage[0]?.date;
    const timeZoneLabel = (siteConfig.timeZoneText?.value || '').replace(
      /\[timezone\]/g,
      storedTimezone || ''
    );
    return (
      <>
        {visibleDays.length === 0 ? (
          <></>
        ) : (
          <>
            <StDateLabel sx={{ mt: '0.75rem' }}>{siteConfig.selectDateTimeText?.value}</StDateLabel>
            <StSiteDetail sx={{ mb: '1.5rem' }}>{timeZoneLabel}</StSiteDetail>
            <StMonthLabel>{getMonthLabel(activeMonthDate)}</StMonthLabel>
            <StDateCarouselWrapper>
              <StNavArrow
                side="left"
                onClick={() => canGoBack && setPageStart((p) => p - 1)}
                sx={{ opacity: canGoBack ? 1 : 0.3, pointerEvents: canGoBack ? 'auto' : 'none' }}
                aria-label="Previous dates "
              >
                <ChevronLeftIcon sx={{ fontSize: 18 }} />
              </StNavArrow>
              <Grid
                container
                wrap="nowrap"
                alignItems="stretch"
                justifyContent="flex-start"
                sx={{ flex: 1, minWidth: 0, columnGap: { xs: '0.25rem', sm: '0.35rem' } }}
              >
                {visibleDays.map((day) => {
                  const globalIdx = days.findIndex((d) => d.date === day.date);
                  const isSelected = globalIdx === selectedDayIndex;
                  const isDisabled = day.slotCount === 0;
                  return (
                    <Grid
                      item
                      xs
                      key={day.date}
                      sx={{
                        display: 'flex',
                        maxWidth: { sm: '120px' },
                      }}
                    >
                      <StDayCell
                        selected={isSelected}
                        disabled={isDisabled}
                        onClick={() => handleDayClick(globalIdx)}
                        aria-disabled={isDisabled}
                        aria-pressed={isSelected}
                      >
                        <span className="day-name">{day.dayLabel}</span>
                        <span className="day-number">{day.dayNumber}</span>
                        <span className="day-slots">
                          {isDisabled
                            ? `0 ${getSlotsLabel(0)}`
                            : `${day.slotCount} ${getSlotsLabel(day.slotCount)}`}
                        </span>
                      </StDayCell>
                    </Grid>
                  );
                })}
              </Grid>
              <StNavArrow
                side="right"
                onClick={() => canGoForward && setPageStart((p) => p + 1)}
                sx={{
                  opacity: canGoForward ? 1 : 0.3,
                  pointerEvents: canGoForward ? 'auto' : 'none',
                }}
                aria-label="Next dates"
              >
                <ChevronRightIcon sx={{ fontSize: 18 }} />
              </StNavArrow>
            </StDateCarouselWrapper>
          </>
        )}
      </>
    );
  };
  const renderTimeSlots = () => (
    <Box sx={{ mt: '1.25rem' }}>
      {timeSlots.length === 0 ? (
        <StSiteDetail sx={{ color: theme.palette.neutral6?.main }} data-testid="no-slots-message">
          {siteConfig?.noSlotsAvailableText?.value ||
            'Sorry. There are no more slots available for this site'}
        </StSiteDetail>
      ) : (
        <StTimeSlotsContainer>
          <Grid container spacing={1.2}>
            {timeSlots.map((slot) => {
              const isSelected = slot.id === selectedSlotId;
              const isSlotDisabled = slot.slotsLeft <= 0;
              const startTimeDisplay = formatTimeToAmPm(slot.startTime);
              const endTimeDisplay = formatTimeToAmPm(slot.endTime);
              return (
                <Grid item xs={12} key={slot.id}>
                  <StTimeSlotItem
                    selected={isSelected}
                    disabled={isSlotDisabled}
                    role="radio"
                    aria-checked={isSelected}
                    aria-disabled={isSlotDisabled}
                    data-testid={`slot-${slot.id}`}
                    onClick={() => {
                      if (isSlotDisabled) return;
                      setSelectedSlotId(slot.id);
                      setStatus('idle');
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
                      <Radio
                        checked={isSelected}
                        disabled={isSlotDisabled}
                        size="small"
                        onChange={() => {
                          if (isSlotDisabled) return;
                          setSelectedSlotId(slot.id);
                          setStatus('idle');
                        }}
                        icon={
                          <RadioButtonUncheckedIcon
                            fontSize="small"
                            sx={{ color: theme.palette.neutral6?.main || '#999' }}
                          />
                        }
                        checkedIcon={
                          <RadioButtonCheckedIcon
                            fontSize="small"
                            sx={{
                              color: isToapplyV2Theme ? themeColor : theme.palette.primary?.main,
                            }}
                          />
                        }
                        sx={{ padding: 0 }}
                        inputProps={{
                          'aria-label': `${startTimeDisplay} to ${endTimeDisplay}`,
                        }}
                      />
                      <StTimeLabel>
                        {startTimeDisplay} &ndash; {endTimeDisplay}
                      </StTimeLabel>
                    </Box>
                    <StSlotsLeftBadge>
                      {slot.slotsLeft} {getSlotsLeftLabel(slot.slotsLeft)}
                    </StSlotsLeftBadge>
                  </StTimeSlotItem>
                </Grid>
              );
            })}
          </Grid>
        </StTimeSlotsContainer>
      )}
    </Box>
  );
  const renderFooter = () => {
    const confirmDisabled = !selectedSlotId || isSubmitting;
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '0.75rem',
          mt: '3rem',
        }}
      >
        {isToapplyV2Theme ? (
          <ButtonPrimary
            disabled={confirmDisabled}
            onClick={handleConfirm}
            data-testid="confirm-button"
            aria-busy={isSubmitting}
            fullWidth={isMobile}
          >
            {siteConfig?.confirmButtonText?.value}
          </ButtonPrimary>
        ) : (
          <StConfirmButton
            disabled={confirmDisabled}
            onClick={handleConfirm}
            data-testid="confirm-button"
            aria-busy={isSubmitting}
          >
            {siteConfig?.confirmButtonText?.value}
          </StConfirmButton>
        )}
        <ButtonDiscreet
          style={{ paddingLeft: '6px' }}
          onClick={handleNoneOfTheseTimes}
          data-testid="none-link"
          aria-busy={isNoneSubmitting}
          disabled={isNoneSubmitting}
          color={isToapplyV2Theme ? themeColor : undefined}
        >
          {siteConfig?.noSlotConvenientLinkText?.value}
        </ButtonDiscreet>
      </Box>
    );
  };
  // ─── Root render ──────────────────────────────────────────────────────────
  return (
    <StScheduleWrapper>
      <StFormCard>
        <Grid container direction="column" wrap="nowrap">
          <Grid item>
            <StHeading variant="h2">{siteConfig?.title?.value}</StHeading>
            <StSubHeading>{siteConfig?.subTitle?.value}</StSubHeading>
          </Grid>
          {status === 'conflict' && <Grid item>{renderAlertBanner()}</Grid>}
          <Grid item>{renderSiteCard()}</Grid>
          <Grid item>{renderDateCarousel()}</Grid>
          <Grid item>{renderTimeSlots()}</Grid>
          <Grid item>{renderFooter()}</Grid>
        </Grid>
      </StFormCard>
    </StScheduleWrapper>
  );
};
export default FOVScheduling;
